import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Send, MessageCircle, AtSign } from 'lucide-react'
import { api } from '@/services/api'
import { useAuthStore } from '@/stores/authStore'
import clsx from 'clsx'

interface Message {
  id: string
  userId: string
  displayName: string
  body: string
  createdAt: string
}

interface LeagueMember {
  id: string
  displayName: string
}

interface GameThreadProps {
  leagueId: string
  gameId: string
  className?: string
}

/* ── Mention highlight renderer ───────────────────────────── */
const renderBody = (body: string) => {
  // Match @DisplayName patterns
  const parts = body.split(/(@\w[\w\s]{0,20}?)(?=\s|$|[.,!?])/g)
  return parts.map((part, i) => {
    if (part.startsWith('@')) {
      return (
        <span key={i} className="text-orange-400 font-semibold bg-orange-500/10 rounded px-0.5">
          {part}
        </span>
      )
    }
    return part
  })
}

const GameThread: React.FC<GameThreadProps> = ({ leagueId, gameId, className }) => {
  const { user } = useAuthStore()
  const [messages, setMessages] = useState<Message[]>([])
  const [newMsg, setNewMsg] = useState('')
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Mention picker state
  const [members, setMembers] = useState<LeagueMember[]>([])
  const [showMentionPicker, setShowMentionPicker] = useState(false)
  const [mentionFilter, setMentionFilter] = useState('')
  const [mentionIdx, setMentionIdx] = useState(0)

  // Fetch league members once
  useEffect(() => {
    const loadMembers = async () => {
      try {
        const res = await api.get<any>(`/leagues/${leagueId}`)
        const memberList = res?.members || res?.league?.members || []
        setMembers(memberList.map((m: any) => ({
          id: m.id || m.userId,
          displayName: m.displayName || m.display_name || m.username || 'Player',
        })).filter((m: any) => m.id !== user?.id))
      } catch {
        // Non-critical
      }
    }
    loadMembers()
  }, [leagueId, user?.id])

  // Fetch messages via API
  const fetchMessages = useCallback(async () => {
    try {
      const res = await api.get<{ messages: Message[] }>(`/leagues/${leagueId}/messages?gameId=${gameId}&limit=50`)
      setMessages(res?.messages || [])
    } catch (e) {
      console.warn('GameThread load error', e)
      setMessages([])
    } finally {
      setLoading(false)
    }
  }, [leagueId, gameId])

  useEffect(() => {
    fetchMessages()
    const interval = setInterval(fetchMessages, 10000)
    return () => clearInterval(interval)
  }, [fetchMessages])

  // Auto-scroll on new messages
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages])

  // Mention detection: watch for @ in input
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setNewMsg(val)

    // Check if cursor is after an @
    const cursorPos = e.target.selectionStart || val.length
    const textBeforeCursor = val.substring(0, cursorPos)
    const atMatch = textBeforeCursor.match(/@(\w*)$/)

    if (atMatch) {
      setMentionFilter(atMatch[1].toLowerCase())
      setShowMentionPicker(true)
      setMentionIdx(0)
    } else {
      setShowMentionPicker(false)
    }
  }

  const filteredMembers = members.filter((m) =>
    m.displayName.toLowerCase().includes(mentionFilter)
  ).slice(0, 5)

  const insertMention = (member: LeagueMember) => {
    const cursorPos = inputRef.current?.selectionStart || newMsg.length
    const textBeforeCursor = newMsg.substring(0, cursorPos)
    const atIdx = textBeforeCursor.lastIndexOf('@')
    const before = newMsg.substring(0, atIdx)
    const after = newMsg.substring(cursorPos)
    setNewMsg(`${before}@${member.displayName} ${after}`)
    setShowMentionPicker(false)
    inputRef.current?.focus()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (showMentionPicker && filteredMembers.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setMentionIdx((i) => Math.min(i + 1, filteredMembers.length - 1))
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setMentionIdx((i) => Math.max(i - 1, 0))
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        insertMention(filteredMembers[mentionIdx])
        return
      }
      if (e.key === 'Escape') {
        setShowMentionPicker(false)
        return
      }
    }
    if (e.key === 'Enter' && !e.shiftKey && !showMentionPicker) {
      handleSend()
    }
  }

  const handleSend = async () => {
    if (!newMsg.trim() || !user || sending) return
    setSending(true)
    try {
      await api.post(`/leagues/${leagueId}/messages`, { body: newMsg.trim(), gameId })
      setNewMsg('')
      setMessages((prev) => [...prev, {
        id: `temp-${Date.now()}`,
        userId: user.id,
        displayName: 'You',
        body: newMsg.trim(),
        createdAt: new Date().toISOString(),
      }])
    } catch (e) {
      console.warn('GameThread send error', e)
    } finally {
      setSending(false)
    }
  }

  const formatTime = (ts: string) => {
    const d = new Date(ts)
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  return (
    <div className={clsx('rounded-xl bg-black/20 border border-white/[0.04]', className)}>
      {/* Messages */}
      <div ref={scrollRef} className="max-h-40 overflow-y-auto p-3 space-y-2">
        {loading ? (
          <p className="text-[10px] text-white/20 text-center py-3">Loading thread…</p>
        ) : messages.length === 0 ? (
          <div className="text-center py-3">
            <MessageCircle className="h-4 w-4 text-white/10 mx-auto mb-1" />
            <p className="text-[10px] text-white/20">Be the first to comment!</p>
          </div>
        ) : (
          messages.map((m) => (
            <div key={m.id} className="flex items-start gap-2">
              <div className="w-5 h-5 rounded-full bg-white/[0.06] flex items-center justify-center shrink-0 mt-0.5">
                <span className="text-[9px] text-white/40">{m.displayName[0]?.toUpperCase()}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-1.5">
                  <span className={clsx(
                    'text-[10px] font-semibold',
                    m.userId === user?.id ? 'text-orange-400' : 'text-white/50'
                  )}>
                    {m.userId === user?.id ? 'You' : m.displayName}
                  </span>
                  <span className="text-[9px] text-white/15">{formatTime(m.createdAt)}</span>
                </div>
                <p className="text-[11px] text-white/70 leading-relaxed">{renderBody(m.body)}</p>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Input with mention picker */}
      <div className="relative">
        {/* Mention picker dropdown */}
        {showMentionPicker && filteredMembers.length > 0 && (
          <div className="absolute bottom-full left-2 right-2 mb-1 bg-gray-900 border border-white/[0.1] rounded-lg shadow-xl overflow-hidden z-10">
            {filteredMembers.map((m, i) => (
              <button
                key={m.id}
                onMouseDown={(e) => { e.preventDefault(); insertMention(m) }}
                className={clsx(
                  'w-full text-left px-3 py-2 text-xs flex items-center gap-2 transition-colors',
                  i === mentionIdx ? 'bg-orange-500/20 text-orange-300' : 'text-white/60 hover:bg-white/[0.06]',
                )}
              >
                <AtSign className="h-3 w-3 text-orange-400/50" />
                <span className="font-medium">{m.displayName}</span>
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center gap-2 p-2 border-t border-white/[0.04]">
          <input
            ref={inputRef}
            type="text"
            value={newMsg}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Talk trash… (@ to mention)"
            className="flex-1 bg-white/[0.04] border border-white/[0.06] rounded-lg px-3 py-1.5 text-xs text-white placeholder-white/20 focus:outline-none focus:border-orange-500/30"
          />
          <button
            onClick={handleSend}
            disabled={!newMsg.trim() || sending}
            className="p-1.5 rounded-lg bg-orange-500 hover:bg-orange-400 disabled:opacity-30 transition-colors"
          >
            <Send className="h-3.5 w-3.5 text-white" />
          </button>
        </div>
      </div>
    </div>
  )
}

export default GameThread
